import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CreateOrder.css';

const CreateOrder = () => {
  const [clients, setClients] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [specialFees, setSpecialFees] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Función para formatear la fecha al formato MySQL DATETIME (YYYY-MM-DD HH:MM:SS)
  const formatToMySQLDateTime = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) {
          setError('Please log in to create orders');
          navigate('/admin-login');
          return;
        }

        // Fetch clients (users)
        const usersResponse = await axios.get('https://valleyapi-dr9y.onrender.com/api/admin/users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClients(usersResponse.data);

        // Fetch admins
        const adminsResponse = await axios.get('https://valleyapi-dr9y.onrender.com/api/admin/admins', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdmins(adminsResponse.data);

        // Fetch countries
        const countriesResponse = await axios.get('https://valleyapi-dr9y.onrender.com/api/countries');
        setCountries(countriesResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (selectedClient) {
      const client = clients.find(c => c.idusers === parseInt(selectedClient));
      if (client) {
        // Safely construct address strings, handling undefined/null values
        const hasShippingAddress = client.shipping_address && client.shipping_address.trim() !== '';
        const hasBillingAddress = client.billing_address && client.billing_address.trim() !== '';
        const hasZipCode = client.zipCode && client.zipCode.trim() !== '';

        const shipping = hasShippingAddress || hasZipCode || client.country
          ? [
              client.shipping_address || '',
              client.zipCode || '',
              client.country || ''
            ].filter(Boolean).join(', ')
          : 'No shipping address available';

        const billing = hasBillingAddress || hasZipCode || client.country
          ? [
              client.billing_address || '',
              client.zipCode || '',
              client.country || ''
            ].filter(Boolean).join(', ')
          : 'No billing address available';

        setShippingAddress(shipping);
        setBillingAddress(billing);

        const countryData = countries.find(c => c.Country === client.country);
        setShippingCost(countryData ? parseFloat(countryData.Shipping_Cost) || 0 : 0);
      } else {
        setShippingAddress('');
        setBillingAddress('');
        setShippingCost(0);
      }
    } else {
      setShippingAddress('');
      setBillingAddress('');
      setShippingCost(0);
    }
  }, [selectedClient, clients, countries]);

  useEffect(() => {
    const newSubtotal = selectedProducts.reduce((sum, p) => {
      const price = parseFloat(p.product_price);
      if (isNaN(price)) {
        console.warn(`Invalid product_price for product ${p.product_name}:`, p.product_price);
        return sum;
      }
      return sum + (price * (p.quantity || 1));
    }, 0);
    setSubtotal(newSubtotal);
    setTotal(newSubtotal + (parseFloat(shippingCost) || 0) + (parseFloat(specialFees) || 0));
  }, [selectedProducts, shippingCost, specialFees]);

  const handleSearch = async () => {
    try {
      const response = await axios.get(`https://valleyapi-dr9y.onrender.com/api/products/search?q=${searchTerm}`);
      console.log('Search API response:', response.data.products);
      const sanitizedProducts = response.data.products.map(product => ({
        ...product,
        product_price: parseFloat(product.product_price) || 0
      }));
      setSearchResults(sanitizedProducts);
    } catch (err) {
      console.error('Error searching products:', err);
      setError('Failed to search products');
    }
  };

  const addProduct = (product) => {
    const price = parseFloat(product.product_price);
    if (isNaN(price)) {
      console.warn(`Invalid product_price when adding product ${product.product_name}:`, product.product_price);
    }
    setSelectedProducts(prev => [...prev, {
      ...product,
      quantity: 1,
      product_price: isNaN(price) ? 0 : price
    }]);
    setSearchTerm('');
    setSearchResults([]);
  };

  const updateQuantity = (id, quantity) => {
    setSelectedProducts(prev => prev.map(p => p.idproducts === id ? { ...p, quantity: parseInt(quantity) || 1 } : p));
  };

  const removeProduct = (id) => {
    setSelectedProducts(prev => prev.filter(p => p.idproducts !== id));
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setError('Please log in to create order');
        navigate('/admin-login');
        return;
      }

      const client = clients.find(c => c.idusers === parseInt(selectedClient));
      if (!client) {
        setError('Please select a client');
        return;
      }

      if (!selectedAdmin) {
        setError('Please select an admin');
        return;
      }

      if (selectedProducts.length === 0) {
        setError('Please add at least one product');
        return;
      }

      if (subtotal === 0) {
        setError('Subtotal cannot be zero');
        return;
      }

      const orderData = {
        idusers: parseInt(selectedClient),
        companyName: client.companyName || '',
        order_state: 'Waiting for Payment',
        subtotal: parseFloat(subtotal) || 0,
        shipping_cost: parseFloat(shippingCost) || 0,
        taxes: parseFloat(specialFees) || 0,
        total: parseFloat(total) || 0,
        date: formatToMySQLDateTime(new Date()), 
        billing_address: billingAddress || '',
        shipping_address: shippingAddress || '',
        managed_by: selectedAdmin || '',
        items: selectedProducts.map(p => ({
          idproducts: parseInt(p.idproducts) || 0,
          quantity: parseInt(p.quantity) || 1,
          product_price: parseFloat(p.product_price) || 0,
          product_name: p.product_name || ''
        })),
      };

      console.log('Order data being sent:', orderData);

      await axios.post('https://valleyapi-dr9y.onrender.com/api/cart/admin/orders', orderData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/admin/orders');
    } catch (err) {
      console.error('Error creating order:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to create order');
    }
  };

  return (
    <div className="create-order-container">
      <h2 className="create-order-title">Create Order</h2>
      {error && <p className="create-order-error">{error}</p>}
      <div className="create-order-form">
        <div className="create-order-form-group">
          <label>Client</label>
          <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)}>
            <option value="">Select Client</option>
            {clients.map(client => (
              <option key={client.idusers} value={client.idusers}>{client.name}</option>
            ))}
          </select>
        </div>
        <div className="create-order-form-group">
          <label>Shipping Address</label>
          <input
            type="text"
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
            placeholder="Enter shipping address"
          />
        </div>
        <div className="create-order-form-group">
          <label>Billing Address</label>
          <input
            type="text"
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            placeholder="Enter billing address"
          />
        </div>
        <div className="create-order-form-group">
          <label>Managed by</label>
          <select value={selectedAdmin} onChange={(e) => setSelectedAdmin(e.target.value)}>
            <option value="">Select Admin</option>
            {admins.map(admin => (
              <option key={admin.idadminusers} value={admin.admin_name}>{admin.admin_name}</option>
            ))}
          </select>
        </div>
        <div className="create-order-form-group">
          <label>Products</label>
          <div className="create-order-search">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="button" onClick={handleSearch}>Search</button>
          </div>
          {searchResults.length > 0 && (
            <ul className="create-order-search-results">
              {searchResults.map(product => (
                <li key={product.idproducts}>
                  {product.product_name} - ${(parseFloat(product.product_price) || 0).toFixed(2)}
                  <button onClick={() => addProduct(product)}>Add</button>
                </li>
              ))}
            </ul>
          )}
          {selectedProducts.length > 0 && (
            <table className="create-order-products-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedProducts.map(p => {
                  const price = parseFloat(p.product_price);
                  if (isNaN(price)) {
                    console.warn(`Rendering product ${p.product_name} with invalid price:`, p.product_price);
                  }
                  return (
                    <tr key={p.idproducts}>
                      <td>{p.product_name}</td>
                      <td>${(isNaN(price) ? 0 : price).toFixed(2)}</td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          value={p.quantity}
                          onChange={(e) => updateQuantity(p.idproducts, e.target.value)}
                        />
                      </td>
                      <td>
                        <button onClick={() => removeProduct(p.idproducts)}>Remove</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="create-order-form-group">
          <label>Subtotal</label>
          <input type="number" value={subtotal.toFixed(2)} readOnly />
        </div>
        <div className="create-order-form-group">
          <label>Shipping Cost</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={shippingCost}
            onChange={(e) => setShippingCost(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="create-order-form-group">
          <label>Special Fees</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={specialFees}
            onChange={(e) => setSpecialFees(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="create-order-form-group">
          <label>Total</label>
          <input type="number" value={total.toFixed(2)} readOnly />
        </div>
        <button type="button" onClick={handleCreate} className="create-order-btn">Create Order</button>
      </div>
    </div>
  );
};

export default CreateOrder;